import { useFeatureFlags } from '@/common/presentation/hooks/useFeatureFlags';
import { useShowErrorSnackbar } from '@/common/application/hooks/useShowErrorSnackbar';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import { useGetApiV3RaptorJobsJobIdTickets } from '@/libs/api-rest/generated/tickets/tickets';
import type { TicketDetailsDto } from '@/libs/api-rest/generated/model';
import type { GetApiV3RaptorJobsJobIdTicketsParams } from '@/libs/api-rest/generated/model';

/**
 * Adapter hook to fetch a single job ticket for ERP jobs
 * For ERP jobs, we only fetch the first ticket (pageSize=1)
 * Uses Orval-generated hooks for type-safe API calls
 * @param jobId - The ID of the job to fetch ticket for
 * @param legacy - Whether this is a legacy (ERP) job - passed as query parameter
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns Query result with single ticket data, loading state, and error handling
 */
export const useJobTicketERP = (jobId: number | undefined, legacy: boolean | undefined, enabled: boolean = true) => {
  // Use useFeatureFlags to get both the flag value and readiness state
  const { values: flagValues, isReady: flagsReady } = useFeatureFlags([FEATURE_FLAGS.ORVAL_ENABLED]);
  const isOrvalEnabled = flagValues[FEATURE_FLAGS.ORVAL_ENABLED] ?? false;

  // Build params object with legacy query parameter
  const params: GetApiV3RaptorJobsJobIdTicketsParams = {
    Page: 1,
    PageSize: 1 // Only fetch one ticket for ERP jobs
  };

  // Add legacy query parameter when it's an ERP job
  if (legacy === true) {
    params.legacy = true;
  }

  const { data, isLoading, error, refetch, ...rest } = useGetApiV3RaptorJobsJobIdTickets(jobId ?? 0, params, {
    query: {
      // Only enable query when flags are ready, flag is enabled, and we have a valid jobId
      enabled: enabled && flagsReady && isOrvalEnabled && !!jobId && jobId > 0,
      // Always fetch fresh data from server (staleTime: 0 means data is immediately stale)
      staleTime: 0,
      queryKey: ['jobTicketERP', jobId, legacy]
    }
  });

  useShowErrorSnackbar(error, 'Failed to load job ticket');

  // Access the response data correctly - axios wraps the response in data property
  const responseData = data?.data;

  // Extract the first ticket from the items array
  const ticket = responseData?.items?.[0] as TicketDetailsDto | undefined;

  return {
    ...rest,
    ticket,
    ticketsResponse: responseData,
    isLoading,
    error,
    refetch
  };
};
